import { computed, nextTick, onMounted, ref, watch } from 'vue';
import {
  addCollaboratorsInOrder,
  changeDate as updateDate,
  Collaborator,
  Deal,
  formatDate,
  formatTime,
  getBonsulyData,
  getCurrentDate,
  getDealsData,
  getOrderComments,
  getRealTimeLeaderboardData,
  getTableHeaders,
  getTodalDeals,
  getTooltipMessage,
  getUserListBySearchKeyword,
  hasUserLiked,
  saveComments,
  toggleLikeAction,
  TotalDeal,
} from './Home';
import { useAuthStore } from '../store/authStore';

export function useHomeComponent() {
  const authStore = useAuthStore();
  const images = import.meta.glob('/src/assets/*.gif', { eager: true });
  const tableHeaders = getTableHeaders();
  const deals = ref<Deal[]>([]);
  const totalDeals = ref<TotalDeal | null>(null);
  const loading = ref(true);
  const tooltiploading = ref(true);
  const dealCommentDialogFullScreen = ref(false);
  const collaboratorDialog = ref(false);
  const activeButton = ref<string>('topDeals');
  const showEmojiPicker = ref(false);
  const showGifPicker = ref(false);
  const selectedGifs = ref<String[]>([]);
  const showSuggestions = ref(false);
  const mentionStartIndex = ref<number>();
  const caretPosition = ref<{}>({ top: 0, left: 0 });
  const isFilterPanelOpen = ref(false);
  const isZIndexApplied = ref(false);
  const currentDate = ref(getCurrentDate());
  const formattedDate = computed(() => formatDate(currentDate.value));
  const gifs = Object.keys(images);
  const superAreas = ['Americas', 'APAC', 'CEMEA', 'EMEA', 'Nordics', 'zCorporate'];
  const segments = ['SMB - Inbound Only', 'Enterprise', 'Mid-Market General Business', 'Mid-Market Commercial'];
  const roles = ['Global Admin', 'CS Big Apple', 'CA Broadway Sales', 'CS Gold Austin', 'CA Boston Sales', 'CA Charlotte Sales'];
  const selectedCollb = ref([]);
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  const selectedOrderId = ref<string>();
  const collaboratorArray = ref<Collaborator[]>([]);
  const users = ref<Collaborator[]>([]);

  const commentMessage = ref<string>('');
  const commentsArray = ref<[]>([]);
  const selectedCommentOrder = ref<Deal>();
  const selectedcommentOrderId = ref<string>();
  const isSubmittingCommention = ref(false);
  let filteredUsers = [{}];
  const scrollContainer = ref<HTMLDivElement | null>(null);
  const commentSkentonloader = ref(true);

  const loadTopDeals = () => loadDealsData(getDealsData, 'topDeals');
  const loadBonsuly = () => loadDealsData(getBonsulyData, 'bonsuly');
  const loadRealTimeLeaderboard = () => loadDealsData(getRealTimeLeaderboardData, 'realTimeLeaderboard');
  const loadTotalDeals = () => loadTodalDealsData(getTodalDeals);
  const loadDealsComment = (item: Deal) => loadDealsCommentData(item, getOrderComments);
  const loadCollob = () => loadAllCollob(getUserListBySearchKeyword);

  onMounted(async () => {
    console.log(import.meta.env.VITE_API_URL);
    loadTopDeals();
    loadTotalDeals();
  });

  const callBothApi = () => {
    loadTopDeals();
    loadTotalDeals();
  };

  const loadDealsData = async (fetchFunction: (filters: { date: string | null; superarea: string; segment: string; role: string }) => Promise<Deal[]>, buttonName: string) => {
    activeButton.value = buttonName;
    loading.value = true;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    deals.value = await fetchFunction({
      date: currentDate.value.toDateString(),
      superarea: filters.value.superarea,
      segment: filters.value.segment,
      role: filters.value.role,
    });
    loading.value = false;
  };

  const loadTodalDealsData = async (fetchTotalDealFunction: (filters: { date: string | null }) => Promise<TotalDeal>) => {
    loading.value = true;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    totalDeals.value = await fetchTotalDealFunction({
      date: currentDate.value.toDateString(),
    });
    loading.value = false;
  };

  const loadDealsCommentData = async (item: Deal, fetchCommentFunction: (item: Deal) => Promise<[]>) => {
    if (commentsArray.value.length == 0) {
      commentSkentonloader.value = true;
    }
    commentsArray.value = await fetchCommentFunction(item);
    commentSkentonloader.value = false;
  };

  const loadAllCollob = async (fetchCollobFunction: () => Promise<Collaborator[]>) => {
    collaboratorArray.value = [];
    await new Promise((resolve) => setTimeout(resolve, 1000));
    collaboratorArray.value = await fetchCollobFunction();
    users.value = collaboratorArray.value;
  };

  const loadTooltipMessage = (item: Deal) => getTooltipMessage(item);

  const openCollaboratorDialog = (item: Deal) => {
    loadCollob();
    selectedOrderId.value = item.sf_order_id;
    selectedCollb.value = item.collaborators.map((collab) => {
      return { id: collab.user_id, name: collab.name };
    });
    collaboratorDialog.value = true;
  };

  const openCommentModel = (item: Deal) => {
    dealCommentDialogFullScreen.value = true;
    selectedcommentOrderId.value = item.sf_order_id;
    selectedCommentOrder.value = item;
    loadCollob();
    loadDealsComment(item);
  };

  const openEmojiModel = () => {
    showEmojiPicker.value = !showEmojiPicker.value;
    if (showGifPicker.value) {
      showGifPicker.value = !showGifPicker.value;
      isZIndexApplied.value = true;
    } else {
      isZIndexApplied.value = !isZIndexApplied.value;
    }
  };

  const toggleGifPicker = () => {
    showGifPicker.value = !showGifPicker.value;
    if (showEmojiPicker.value) {
      showEmojiPicker.value = !showEmojiPicker.value;
      isZIndexApplied.value = true;
    } else {
      isZIndexApplied.value = !isZIndexApplied.value;
    }
  };

  const toggleFilterPanel = () => {
    isFilterPanelOpen.value = !isFilterPanelOpen.value;
  };

  const closeCommentDialog = () => {
    commentsArray.value = [];
    dealCommentDialogFullScreen.value = false;
  };

  const changeDate = (direction: number) => {
    currentDate.value = updateDate(currentDate.value, direction);
    filters.value.date = new Date(currentDate.value);
    callBothApi();
  };

  const setToday = () => {
    currentDate.value = getCurrentDate();
    filters.value.date = new Date(currentDate.value);
    callBothApi();
  };

  const filters = ref({
    date: currentDate.value,
    superarea: '',
    segment: '',
    role: '',
  });

  const clearFilters = () => {
    filters.value = {
      date: currentDate.value,
      superarea: '',
      segment: '',
      role: '',
    };
    callBothApi();
  };

  const filterCount = computed(() => {
    return Object.entries(filters.value).filter(([key, value]) => key !== 'date' && value).length;
  });

  const handleMouseOver = async () => {
    tooltiploading.value = true;
    await new Promise((resolve) => setTimeout(resolve, 1000));
    tooltiploading.value = false;
  };

  const handleLikeToggle = async (item: Deal) => {
    try {
      await toggleLikeAction(item, authStore.userEmail);
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const onSelectEmoji = (emoji) => {
    commentMessage.value += emoji.i;
    showEmojiPicker.value = !showEmojiPicker.value;
  };

  const addGif = (gif: string) => {
    if (!selectedGifs.value.includes(gif)) {
      selectedGifs.value.push(gif);
    }
    showGifPicker.value = !showGifPicker.value;
  };

  const removeGif = (index) => {
    selectedGifs.value.splice(index, 1);
  };

  const isUserLiked = (item: Deal) => hasUserLiked(item, authStore.userEmail);

  const scrollToBottom = async () => {
    await nextTick();
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
    }
  };

  const updateCaretPosition = (textarea, cursorPosition) => {
    const rect = textarea.getBoundingClientRect();
    const lineHeight = 4;
    const lines = textarea.value.slice(0, cursorPosition).split('\n');
    const top = -rect.x + lineHeight * (lines.length - 1);
    const left = lines[lines.length - 1].length * 7;
    caretPosition.value = { top, left };
  };

  const selectUser = (user) => {
    const beforeMention = commentMessage.value.slice(0, mentionStartIndex.value);
    const afterMention = commentMessage.value.slice(mentionStartIndex.value).split(' ')[1] || '';
    commentMessage.value = `${beforeMention}@[${user.name}](userId:${user.id})${afterMention}`;
    showSuggestions.value = false;
  };

  const handleInput = (event) => {
    const value = event.target.value;
    const cursorPosition = event.target.selectionStart;
    const lastAtIndex = value.lastIndexOf('@', cursorPosition - 1);
    if (lastAtIndex !== -1) {
      const afterAtText = value.slice(lastAtIndex + 1, cursorPosition);
      filteredUsers = users.value.filter((user) =>
        user.name.toLowerCase().includes(afterAtText.toLowerCase())
      );
      if (filteredUsers.length > 0) {
        showSuggestions.value = true;
        mentionStartIndex.value = lastAtIndex;
        updateCaretPosition(event.target, cursorPosition);
      } else {
        showSuggestions.value = false;
      }
    } else {
      showSuggestions.value = false;
    }
  };

  const addCollaborator = async () => {
    collaboratorDialog.value = false;
    loading.value = true;
    await addCollaboratorsInOrder(selectedOrderId.value, selectedCollb.value, authStore.userEmail);
    loadTopDeals();
    loadTotalDeals();
  };

  const submitComment = async (sf_order_id, commentText) => {
    isSubmittingCommention.value = true;
    await saveComments(commentText, sf_order_id, 0, 'Order', authStore.userEmail, selectedGifs.value);
    if (selectedCommentOrder.value) {
      loadDealsComment(selectedCommentOrder.value);
    }

    commentMessage.value = '';
    selectedGifs.value = [];
    isSubmittingCommention.value = false;
    scrollToBottom();
  };

  const handleAddReply = () => {
    if (selectedCommentOrder.value) {
      loadDealsComment(selectedCommentOrder.value);
    }
  };

  watch(() => filters.value.date, (newDate) => {
    if (newDate != null) {
      currentDate.value = updateDate(newDate, 0);
    }
  });

  watch(commentsArray, () => {
    scrollToBottom();
  });

  return {
    authStore,
    tableHeaders,
    deals,
    totalDeals,
    loading,
    tooltiploading,
    dealCommentDialogFullScreen,
    collaboratorDialog,
    activeButton,
    showEmojiPicker,
    showGifPicker,
    selectedGifs,
    showSuggestions,
    mentionStartIndex,
    caretPosition,
    isFilterPanelOpen,
    isZIndexApplied,
    currentDate,
    formattedDate,
    gifs,
    superAreas,
    segments,
    roles,
    selectedCollb,
    formatter,
    selectedOrderId,
    collaboratorArray,
    users,
    commentMessage,
    commentsArray,
    selectedCommentOrder,
    selectedcommentOrderId,
    isSubmittingCommention,
    filteredUsers,
    scrollContainer,
    commentSkentonloader,
    callBothApi,
    loadBonsuly,
    loadRealTimeLeaderboard,
    loadTooltipMessage,
    openCollaboratorDialog,
    openCommentModel,
    openEmojiModel,
    toggleGifPicker,
    toggleFilterPanel,
    closeCommentDialog,
    changeDate,
    setToday,
    filters,
    clearFilters,
    filterCount,
    handleMouseOver,
    handleLikeToggle,
    onSelectEmoji,
    addGif,
    removeGif,
    isUserLiked,
    selectUser,
    handleInput,
    addCollaborator,
    submitComment,
    handleAddReply,
  };
}

